import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SimulationEnvironment = () => {
  const [selectedSimulation, setSelectedSimulation] = useState(null);

  const simulations = [
    {
      id: 1,
      title: 'NASFUND Processing Simulation',
      description: 'Practice calculating and processing NASFUND contributions for a sample PNG company with 50 employees across different salary bands.',
      difficulty: 'intermediate',
      duration: '30 min',
      scenario: 'Process monthly NASFUND contributions for Pacific Trading Ltd with employees earning between K800 - K15,000 per month.',
      icon: 'Calculator',
      color: 'from-blue-600 to-blue-700'
    },
    {
      id: 2,
      title: 'SWT Calculation Workflow',
      description: 'Master Salary & Wages Tax calculations with progressive tax rates and automated filing to PNG Internal Revenue Commission.',
      difficulty: 'advanced',
      duration: '45 min',
      scenario: 'Calculate SWT for 100 employees with varying income levels, allowances, and deductions according to PNG tax brackets.',
      icon: 'FileText',
      color: 'from-purple-600 to-purple-700'
    },
    {
      id: 3,
      title: 'Employment Act Compliance Check',
      description: 'Identify and remediate PNG Employment Act 1978 violations in employee contracts, leave policies, and termination procedures.',
      difficulty: 'intermediate',
      duration: '40 min',
      scenario: 'Audit 25 employee contracts for compliance with minimum wage, leave entitlements, and notice period requirements.',
      icon: 'Shield',
      color: 'from-green-600 to-green-700'
    },
    {
      id: 4,
      title: 'Payroll Processing End-to-End',
      description: 'Complete payroll cycle from timesheet import to payment processing, including all PNG statutory deductions and government filings.',
      difficulty: 'advanced',
      duration: '60 min',
      scenario: 'Process fortnightly payroll for 75 employees including overtime, allowances, NASFUND, SWT, and bank file generation.',
      icon: 'DollarSign',
      color: 'from-emerald-600 to-emerald-700'
    },
    {
      id: 5,
      title: 'Leave Management Scenario',
      description: 'Handle complex leave requests including annual leave, sick leave, and PNG public holidays with compliance validation.',
      difficulty: 'beginner',
      duration: '25 min',
      scenario: 'Approve and process 15 leave requests while maintaining minimum staffing levels and PNG Employment Act compliance.',
      icon: 'Calendar',
      color: 'from-amber-600 to-amber-700'
    },
    {
      id: 6,
      title: 'DLIR Submission Pack Creation',
      description: 'Generate and submit complete DLIR filing packages with employment statistics, compliance reports, and supporting documentation.',
      difficulty: 'advanced',
      duration: '50 min',
      scenario: 'Prepare quarterly DLIR submission for government department with 200+ employees and multiple locations.',
      icon: 'Upload',
      color: 'from-red-600 to-red-700'
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'text-success bg-success/10 border-success/20';
      case 'intermediate':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'advanced':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="bg-primary/10 p-3 rounded-lg">
            <Icon name="Monitor" size={32} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Interactive Simulation Environment
            </h2>
            <p className="text-muted-foreground mb-4">
              Practice with realistic PNG company data and scenarios. All simulations use sample data and won't affect your production environment.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-success">
                <Icon name="CheckCircle" size={16} color="currentColor" />
                <span>Safe Practice Environment</span>
              </div>
              <div className="flex items-center gap-2 text-primary">
                <Icon name="Database" size={16} color="currentColor" />
                <span>Realistic PNG Data</span>
              </div>
              <div className="flex items-center gap-2 text-warning">
                <Icon name="Award" size={16} color="currentColor" />
                <span>Hands-On Learning</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Simulations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {simulations?.map((simulation) => (
          <div
            key={simulation?.id}
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all"
          >
            {/* Header with gradient */}
            <div className={`bg-gradient-to-r ${simulation?.color} p-6 text-white`}>
              <div className="flex items-start justify-between mb-3">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                  <Icon name={simulation?.icon} size={28} color="white" />
                </div>
                <div className={`px-3 py-1 rounded-full border border-white/30 text-xs font-semibold capitalize bg-white/20 backdrop-blur-sm`}>
                  {simulation?.difficulty}
                </div>
              </div>
              <h3 className="text-xl font-heading font-bold mb-2">
                {simulation?.title}
              </h3>
              <div className="flex items-center gap-2 text-sm opacity-90">
                <Icon name="Clock" size={14} color="white" />
                <span>{simulation?.duration}</span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-muted-foreground mb-4">
                {simulation?.description}
              </p>

              {/* Scenario Box */}
              <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Icon name="FileText" size={16} color="var(--color-primary)" className="mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground mb-1">Scenario:</p>
                    <p className="text-xs text-muted-foreground">{simulation?.scenario}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <Button
                fullWidth
                variant="default"
                iconName="Play"
                onClick={() => setSelectedSimulation(simulation?.id)}
              >
                Launch Simulation
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Info Banner */}
      <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">About Simulations</h4>
            <p className="text-sm text-muted-foreground">
              All simulations use realistic PNG company data but operate in a safe sandbox environment. Your progress is tracked and contributes to your certification requirements. Complete simulations to unlock advanced training modules.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationEnvironment;